/**
 * The caller's standing conference, browser side (2026-10-06).
 *
 * Server side: ConferenceRoomService. When CONFERENCE_ROOM_ENABLED is on for
 * the caller, the browser joins ONE Twilio conference at the start of a
 * parallel session (device.connect with revcallRoom=join) and stays in it.
 * Each answered prospect is put into that conference by Twilio; the server
 * announces it with the SSE event call:bridged. There is then no incoming
 * call per prospect: ConferenceLeg stands in for it, with the subset of the
 * Twilio Call API the dialer uses (accept, on, mute, isMuted, disconnect),
 * so the code that handles an accepted call runs unchanged.
 *
 * Switched off (the default), ensure() answers false at once and nothing
 * else in this file runs: the classic incoming-call flow stays as it is.
 */

type Handler = (...args: unknown[]) => void;

export interface RoomCallLike {
  mute(state: boolean): void;
  disconnect(): void;
  on(event: string, cb: Handler): unknown;
}

export interface DeviceLike {
  connect(opts: { params: Record<string, string> }): Promise<RoomCallLike>;
}

/** One prospect inside the caller's conference, seen as a Twilio Call. */
export class ConferenceLeg {
  readonly isConferenceLeg = true;
  private readonly handlers = new Map<string, Handler[]>();
  private muted = false;
  private ended = false;

  constructor(
    readonly callId: string,
    readonly externalCallId: string,
    private readonly roomCall: () => RoomCallLike | null,
    private readonly hangup: (callSid: string) => Promise<void>,
  ) {}

  on(event: string, cb: Handler): this {
    const list = this.handlers.get(event) ?? [];
    list.push(cb);
    this.handlers.set(event, list);
    return this;
  }

  private emit(event: string, ...args: unknown[]): void {
    for (const cb of this.handlers.get(event) ?? []) {
      try { cb(...args); } catch { /* one listener must not stop the others */ }
    }
  }

  /** The prospect is already in the conference: accepting is immediate. */
  accept(): void {
    // A microtask, not setTimeout: Chrome holds timers up to 1 s in a
    // background tab, which delayed the card that much (2026-10-07).
    queueMicrotask(() => this.emit('accept'));
  }

  /** Mutes the caller's microphone, which is the conference link. */
  mute(state: boolean): void {
    this.muted = state;
    this.roomCall()?.mute(state);
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** The caller hangs up: only the prospect's line ends, the conference stays. */
  disconnect(): void {
    if (this.ended) return;
    void this.hangup(this.externalCallId);
    this.finish();
  }

  /** The prospect's line ended on Twilio's side (SSE call:completed or call:voicemail). */
  endedRemotely(): void {
    this.finish();
  }

  isEnded(): boolean {
    return this.ended;
  }

  private finish(): void {
    if (this.ended) return;
    this.ended = true;
    // The next prospect must not inherit a muted microphone.
    if (this.muted) {
      try { this.roomCall()?.mute(false); } catch { /* the link may be gone */ }
      this.muted = false;
    }
    this.emit('disconnect');
  }
}

export interface ConferenceRoomDeps {
  /** POST /api/twilio/conference-room */
  status: (identity: string) => Promise<{ enabled: boolean; ready: boolean }>;
  device: () => DeviceLike | null;
  identity: () => string | null;
  sleep?: (ms: number) => Promise<void>;
  log?: (...args: unknown[]) => void;
}

/** Joins, keeps and leaves the caller's conference. */
export class ConferenceRoomClient {
  private call: RoomCallLike | null = null;
  private wanted = false;
  private joining: Promise<boolean> | null = null;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly log: (...args: unknown[]) => void;

  constructor(private readonly deps: ConferenceRoomDeps) {
    this.sleep = deps.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.log = deps.log ?? ((...a) => console.log(...a));
  }

  roomCall(): RoomCallLike | null {
    return this.call;
  }

  isActive(): boolean {
    return !!this.call;
  }

  /**
   * Before a parallel dial: true when the conference is up and Twilio has
   * confirmed this device inside it, so the dial will use it. False when it
   * is switched off, or did not come up within maxWaitMs: the dial then
   * takes the classic flow, nothing is lost.
   */
  async ensure(maxWaitMs = 3000): Promise<boolean> {
    const identity = this.deps.identity();
    const device = this.deps.device();
    if (!identity || !device) return false;
    let st: { enabled: boolean; ready: boolean };
    try { st = await this.deps.status(identity); } catch { return false; }
    if (!st.enabled) return false;
    this.wanted = true;
    if (st.ready && this.call) return true;
    if (!this.joining && this.call && !st.ready) {
      // The browser is still in a conference the server no longer knows
      // (the API restarted, e.g. a deploy): leave it and join again, or the
      // dial would silently fall back to the classic flow (2026-10-06).
      const stale = this.call;
      this.call = null;
      try { stale.disconnect(); } catch { /* already gone */ }
      this.log('[ConferenceRoom] server lost the conference, rejoining');
    }
    if (!this.joining) this.joining = this.join(identity, device, maxWaitMs).finally(() => { this.joining = null; });
    return this.joining;
  }

  /** End of session: leave the conference, which closes it. */
  leave(): void {
    this.wanted = false;
    const call = this.call;
    this.call = null;
    if (call) {
      try { call.disconnect(); } catch { /* already gone */ }
    }
  }

  private async join(identity: string, device: DeviceLike, maxWaitMs: number): Promise<boolean> {
    if (!this.call) {
      try {
        const call = await device.connect({ params: { revcallRoom: 'join' } });
        this.call = call;
        call.on('disconnect', () => this.dropped(call));
        call.on('error', () => this.dropped(call));
        this.log('[ConferenceRoom] joining');
      } catch (e) {
        this.log('[ConferenceRoom] connect failed', (e as Error)?.message);
        return false;
      }
    }
    const step = 250;
    for (let waited = 0; waited <= maxWaitMs; waited += step) {
      try {
        const st = await this.deps.status(identity);
        if (st.ready) { this.log('[ConferenceRoom] ready'); return true; }
      } catch { /* retry */ }
      await this.sleep(step);
    }
    this.log('[ConferenceRoom] not ready in time, classic flow for this dial');
    return false;
  }

  private dropped(call: RoomCallLike): void {
    if (this.call !== call) return;
    this.call = null;
    this.log('[ConferenceRoom] link dropped', this.wanted ? '(will rejoin at the next dial)' : '');
  }
}

interface CallWithStatus { status?: () => string }

/**
 * Is the caller already in a conversation when a call comes in?
 *
 * The Twilio SDK answered this itself (allowIncomingWhileBusy: false): any
 * open call made the device ignore the invite. The standing conference is an
 * open call for the whole session, so the device now lets every invite
 * through and this function decides, with the same rule minus the
 * conference: another call that is neither pending nor closed, or a prospect
 * inside the conference.
 */
export function isBusyWithAnotherCall(
  incoming: unknown,
  device: { calls?: Iterable<unknown> | ArrayLike<unknown> } | null | undefined,
  roomCall: unknown,
  leg: ConferenceLeg | null | undefined,
): boolean {
  if (leg && !leg.isEnded()) return true;
  const calls = device?.calls ? Array.from(device.calls as Iterable<unknown>) : [];
  return calls.some((c) => {
    if (c === incoming || c === roomCall) return false;
    const status = typeof (c as CallWithStatus).status === 'function' ? (c as CallWithStatus).status!() : '';
    return status !== 'pending' && status !== 'closed';
  });
}
